import type {
  AssetReviewItem,
  AssetReviewSnapshot,
  AssetReviewStage,
} from '@volley/crucible-ipc-types';

/**
 * crucible#2868 (Stream A / AC-1) — the style-lock predicate.
 *
 * The pure, React-free, IPC-free projection that decides which Art-tab surface a
 * game lands on: a FRESH game (style not yet locked) opens the guided
 * stage-by-stage flow (`ArtGuidedFlow`); an EXISTING game (style locked) opens
 * the Library grid (`ArtLibrary`). Sibling of `asset-stage-view.ts`'s
 * `currentStageView` — same posture, node-env unit-tested in
 * `art-style-lock.test.ts`.
 */

/**
 * The stage kinds that DEFINE the game's locked style. Post-crucible#2740 the
 * style anchor is always an `'anchor'`-kind stage; a legacy `'moodboard'`
 * pick-one stage played the same gating role, so both count. This reads the
 * structured `stage.kind` field the producer emits (`stageKindForAsset` in
 * crucible's `asset-stages.ts`) — it is not a semantic guess off free text.
 */
export const STYLE_ANCHOR_STAGE_KINDS: ReadonlySet<string> = new Set(['anchor', 'moodboard']);

/**
 * The gating style-anchor stage in this snapshot, or null if none exists yet
 * (an empty or pre-staged snapshot). A single snapshot CAN carry more than one
 * style-defining stage — `deriveSnapshotStages` emits both a `moodboard` stage
 * AND an `anchor` stage when the manifest has a moodboard pick alongside a
 * flagged anchor (crucible's `asset-stages.test.ts` "preserves the canonical
 * STAGE_ORDER": `['moodboard','anchor',…]`). The style isn't locked until EVERY
 * anchor-kind stage is behind `currentStageIndex`, so the gate is the LAST
 * (highest-index) style-defining stage, not the first. Returning the first
 * would flip the game to "locked" as soon as `moodboard` was approved but while
 * the real `anchor` stage was still active. Stages are dense and index-ordered,
 * so the last match in array order is the highest-index one.
 */
export function styleAnchorStage(snapshot: AssetReviewSnapshot): AssetReviewStage | null {
  let anchor: AssetReviewStage | null = null;
  for (const s of snapshot.stages ?? []) {
    if (STYLE_ANCHOR_STAGE_KINDS.has(s.kind)) anchor = s;
  }
  return anchor;
}

/**
 * Is the game's style locked? True once the style-anchor stage has been passed —
 * its index is BEHIND `currentStageIndex`, which only advances off a stage once
 * that stage is satisfied (the anchor is satisfied by `approved > 0`). This is
 * the single predicate that flips a game from "fresh" (guided flow) to
 * "existing" (Library grid). No stages / no anchor yet ⇒ not locked (a fresh
 * game still needs to lock its look before anything else can be made).
 */
export function isStyleLocked(snapshot: AssetReviewSnapshot): boolean {
  const anchor = styleAnchorStage(snapshot);
  if (!anchor) return false;
  return anchor.index < snapshot.currentStageIndex;
}

/**
 * Should the Art tab land on the GUIDED flow (vs the maintenance Library)?
 *
 * Guide ONLY when there is an unsatisfied MANDATORY style gate — a style-anchor
 * stage exists AND it is not yet locked. Every other state goes to the Library:
 * - anchor exists + locked → Library (existing game, maintenance).
 * - NO anchor stage at all (a done/built game whose manifest never carried an
 *   isStyleAnchor/moodboard need, or a code-first manifest) → Library.
 *
 * This is the landing counterpart to `finishGateSatisfied` (crucible#2872): the
 * guided flow is the from-scratch gate-enforcement surface, so it must only be
 * the landing while the gate is actually pending. Keying the landing on
 * `isStyleLocked` alone trapped a no-anchor game in the guided flow forever
 * (isStyleLocked is false when there's no anchor) even after it was fully built.
 *
 * Empty-wall safety: a genuinely fresh Foundry game HAS an anchor stage (07's
 * derive emits the isStyleAnchor need), so it guides — no empty Library wall.
 * Only a no-anchor manifest reaches the Library; a no-anchor + empty manifest
 * degrades to the Library's own empty-state (maintenance copy), which is
 * correct, not a crash.
 */
export function shouldGuideArtFlow(snapshot: AssetReviewSnapshot): boolean {
  return styleAnchorStage(snapshot) !== null && !isStyleLocked(snapshot);
}

/**
 * Is the review far enough along that ArtGuidedFlow may OFFER "Finish art"?
 * This is the snapshot-derived readiness gate only — the caller still requires
 * the review to be neither in-flight (`finishing`) nor already terminal
 * (`snapshot.done`).
 *
 * - WITH a style anchor: the mandatory style gate — finish only once the anchor
 *   is locked (`isStyleLocked`). This is the non-skippable moodboard/anchor gate.
 * - WITH NO anchor stage: there is no style to lock, so gating on `isStyleLocked`
 *   (false forever) would trap the review — finish stays disabled even after
 *   everything is approved (crucible#2872 Bugbot High). Mirror AssetReviewPanel's
 *   "ship what you've kept" behavior instead: allow finish once at least one
 *   asset is approved, so an empty / still-generating / nothing-approved review
 *   can't offer an enabled Finish.
 *
 * Why `approved > 0` and not `currentStageView.allStagesApproved`: buildSnapshot
 * sets `snapshot.done` once every stage is approved (`stagedComplete`), so an
 * all-stages-approved gate would coincide with the caller's `!done` block,
 * leaving no enabled window (the trap again). `approved > 0` gives a real
 * "some kept, not yet done" window, matching AssetReviewPanel's ship-anytime intent.
 */
export function finishGateSatisfied(snapshot: AssetReviewSnapshot): boolean {
  if (styleAnchorStage(snapshot) !== null) return isStyleLocked(snapshot);
  return snapshot.approved > 0;
}

/**
 * crucible#3803 — did the engine REFUSE this item, rather than simply not having
 * generated it yet? `'blocked'` is the engine's "I will not stage this" verdict
 * (see `build-asset-task.ts`'s `WaitForStageApproval` docblock); `'pending'` is
 * "not generated yet, still coming."
 *
 * The two used to render identically on the guided surface, which is the whole
 * reason a refused asset read as "still working" and the user waited for
 * something that could never arrive. This reads the STRUCTURED `status` field the
 * proxy already carries end-to-end (`AssetReviewItemStatus` includes `'blocked'`;
 * `normalizeAssetStatus` preserves it) — deliberately NOT the `reviewable` flag,
 * which is false for `blocked` and `pending` alike and so cannot tell them apart.
 */
export function isUnservable(item: AssetReviewItem): boolean {
  return item.status === 'blocked';
}

/**
 * crucible#3803 — is the MANDATORY style gate unsatisfiable because the engine
 * refused every member of the anchor stage?
 *
 * The gate is not skippable by design (everything downstream img2img-conditions
 * against the locked look), and this predicate does NOT make it skippable: it
 * only lets the surface say WHY it is stuck and point at the one action that can
 * still rescue it (ask for a change → regenerate). `false` once the style is
 * locked, or with no anchor stage at all.
 *
 * "Every member refused" is deliberately strict. A stage with one refused member
 * alongside a servable or still-`pending` one is NOT stuck — the servable member
 * can carry the stage (`currentStageView`'s solo cost guard already surfaces it),
 * and a `pending` member's take may still land. Reporting those as stuck would
 * overcorrect into blaming a whole stage for one bad asset.
 */
export function styleAnchorUnservable(snapshot: AssetReviewSnapshot): boolean {
  const anchor = styleAnchorStage(snapshot);
  if (!anchor || isStyleLocked(snapshot)) return false;
  const members = snapshot.assets.filter((a) => a.stageIndex === anchor.index);
  return members.length > 0 && members.every(isUnservable);
}
